export const CHARACTER_SECTIONS = [
    { stateKey: 'characterData', dataKey: 'character_data', lastChangeKey: 'character_data_last_change' },
    { stateKey: 'stickyWindows', dataKey: 'sticky_windows', lastChangeKey: 'sticky_windows_last_change' },
    { stateKey: 'maneuverTracker', dataKey: 'maneuver_tracker', lastChangeKey: 'maneuver_tracker_last_change' },
    { stateKey: 'spellTracker', dataKey: 'spell_tracker', lastChangeKey: 'spell_tracker_last_change' },
    { stateKey: 'psyche', dataKey: 'psyche', lastChangeKey: 'psyche_last_change' },
];

export function cloneJson(value) {
    if (value === undefined) return undefined;
    return JSON.parse(JSON.stringify(value));
}

export function parseSection(value) {
    if (typeof value === 'string') return JSON.parse(value);
    return cloneJson(value);
}

export function snapshotFromRow(row) {
    const snapshot = {};
    CHARACTER_SECTIONS.forEach(({ dataKey }) => {
        snapshot[dataKey] = parseSection(row[dataKey]);
    });
    return snapshot;
}

function isObject(value) {
    return value !== null && typeof value === 'object';
}

function valuesEqual(left, right) {
    if (left === right) return true;
    if (!isObject(left) || !isObject(right)) return false;
    return JSON.stringify(left) === JSON.stringify(right);
}

function diffValue(base, current, section, path, patches) {
    if (valuesEqual(base, current)) return;

    if (current === undefined) {
        patches.push({ section, path, op: 'remove' });
        return;
    }

    const baseIsArray = Array.isArray(base);
    const currentIsArray = Array.isArray(current);
    const baseIsObject = isObject(base) && !baseIsArray;
    const currentIsObject = isObject(current) && !currentIsArray;

    if (baseIsArray && currentIsArray) {
        if (base.length !== current.length) {
            let commonPrefix = 0;
            while (
                commonPrefix < base.length
                && commonPrefix < current.length
                && valuesEqual(base[commonPrefix], current[commonPrefix])
            ) {
                commonPrefix += 1;
            }

            let commonSuffix = 0;
            while (
                commonSuffix < base.length - commonPrefix
                && commonSuffix < current.length - commonPrefix
                && valuesEqual(
                    base[base.length - commonSuffix - 1],
                    current[current.length - commonSuffix - 1]
                )
            ) {
                commonSuffix += 1;
            }

            const removedCount = base.length - commonPrefix - commonSuffix;
            const addedCount = current.length - commonPrefix - commonSuffix;
            if (addedCount === 0) {
                for (
                    let index = commonPrefix + removedCount - 1;
                    index >= commonPrefix;
                    index -= 1
                ) {
                    patches.push({
                        section,
                        path: path.concat(String(index)),
                        op: 'remove',
                        oldValue: cloneJson(base[index]),
                    });
                }
                return;
            }

            if (removedCount === 0 && commonPrefix === base.length) {
                for (let index = commonPrefix; index < current.length; index += 1) {
                    patches.push({
                        section,
                        path,
                        op: 'append',
                        value: cloneJson(current[index]),
                    });
                }
                return;
            }

            patches.push({ section, path, op: 'set', value: cloneJson(current) });
            return;
        }

        for (let index = 0; index < base.length; index += 1) {
            diffValue(base[index], current[index], section, path.concat(String(index)), patches);
        }
        return;
    }

    if (baseIsObject && currentIsObject) {
        Object.keys(base).forEach(key => {
            if (!Object.prototype.hasOwnProperty.call(current, key)) {
                patches.push({ section, path: path.concat(key), op: 'remove' });
            }
        });
        Object.keys(current).forEach(key => {
            diffValue(base[key], current[key], section, path.concat(key), patches);
        });
        return;
    }

    patches.push({
        section,
        path,
        op: 'set',
        value: cloneJson(current),
    });
}

export function createCharacterPatches(serverSnapshot, character) {
    const patches = [];
    CHARACTER_SECTIONS.forEach(({ stateKey, dataKey }) => {
        diffValue(serverSnapshot[dataKey], character[stateKey], dataKey, [], patches);
    });
    return patches;
}

export function createSnapshotPatches(baseSnapshot, currentSnapshot) {
    const patches = [];
    CHARACTER_SECTIONS.forEach(({ dataKey }) => {
        diffValue(
            baseSnapshot[dataKey],
            currentSnapshot[dataKey],
            dataKey,
            [],
            patches
        );
    });
    return patches;
}

function setAtPath(root, path, value) {
    if (path.length === 0) return cloneJson(value);

    let target = root;
    for (let index = 0; index < path.length - 1; index += 1) {
        const key = path[index];
        const nextKey = path[index + 1];
        if (!isObject(target[key])) {
            target[key] = /^\d+$/.test(nextKey) ? [] : {};
        }
        target = target[key];
    }
    target[path[path.length - 1]] = cloneJson(value);
    return root;
}

function removeAtPath(root, path, oldValue) {
    if (path.length === 0) return root;

    let target = root;
    for (let index = 0; index < path.length - 1; index += 1) {
        target = target[path[index]];
        if (!isObject(target)) return root;
    }

    const key = path[path.length - 1];
    if (Array.isArray(target)) {
        let removeIndex = Number(key);
        if (oldValue !== undefined && !valuesEqual(target[removeIndex], oldValue)) {
            removeIndex = target.findIndex(item => valuesEqual(item, oldValue));
        }
        if (removeIndex >= 0) target.splice(removeIndex, 1);
    }
    else delete target[key];
    return root;
}

function appendAtPath(root, path, value) {
    if (path.length === 0) {
        const result = Array.isArray(root) ? root : [];
        result.push(cloneJson(value));
        return result;
    }

    let target = root;
    for (let index = 0; index < path.length; index += 1) {
        const key = path[index];
        if (!isObject(target[key])) target[key] = [];
        target = target[key];
    }
    target.push(cloneJson(value));
    return root;
}

export function applyCharacterPatches(snapshot, patches) {
    const result = cloneJson(snapshot) || {};
    patches.forEach(patch => {
        const sectionValue = result[patch.section];
        if (patch.op === 'remove') {
            result[patch.section] = removeAtPath(sectionValue, patch.path, patch.oldValue);
        } else if (patch.op === 'append') {
            result[patch.section] = appendAtPath(sectionValue, patch.path, patch.value);
        } else {
            result[patch.section] = setAtPath(sectionValue, patch.path, patch.value);
        }
    });
    return result;
}
